<script setup lang="ts">
import type { Album, EventVault } from "@/models"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { computed, inject } from "vue"
import { z } from "zod"
import { useGenerateZip } from "@/api"
import { DrawerContextCard, ZipStateSection } from "@/components"
import { Alert, Button, Drawer, Icon, NoData, Switch } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { EventVaultIdKey } from "@/providers/keys"
import { ZipExportDrawerKey } from "@/symbols"
import { useToast } from "../ui/ToastReka/useToast"

interface ZipExportDrawerProps {
  album?: Album
  eventVault?: EventVault
  isLoading?: boolean
  drawerKey?: symbol
  hideFavourites?: boolean
}

const props = withDefaults(defineProps<ZipExportDrawerProps>(), {
  isLoading: false,
  drawerKey: ZipExportDrawerKey,
  hideFavourites: false,
})

const emit = defineEmits<{
  (e: "zipRequested"): void
}>()

const injectedEventVaultId = inject(EventVaultIdKey, undefined)

const toast = useToast()

const { toggle } = useModal(props.drawerKey)

const eventVaultId = computed(() => injectedEventVaultId ?? props.eventVault?.id ?? props.album?.eventVaultId)
const zipSummary = computed(() => (props.album?.zipSummary ? props.album?.zipSummary : props.eventVault?.zipSummary))

const { t } = useTranslation()

const validationSchema = toTypedSchema(
  z.object({
    favouritesOnly: z.boolean().optional(),
    groupByAlbum: z.boolean().optional(),
    groupByGuest: z.boolean().optional(),
  }),
)

const { values, handleSubmit } = useForm({
  validationSchema,
  keepValuesOnUnmount: true,
  initialValues: {
    favouritesOnly: false,
    groupByAlbum: true,
    groupByGuest: true,
  },
})

const isFavouritesOnly = computed(() => Boolean(values.favouritesOnly))

const { mutate: mutateGenerateZip, isPending } = useGenerateZip()

const isGeneratingZip = computed(() => isPending.value)
const isZipPending = computed(() => Boolean((zipSummary.value?.id && !zipSummary.value?.downloadUrl) || isGeneratingZip.value))
const hasDownloadUrl = computed(() => Boolean(zipSummary.value?.downloadUrl))

const onGenerateZip = handleSubmit(() => {
  if (!eventVaultId.value) {
    toast.error()
    return
  }

  mutateGenerateZip({ eventVaultId: eventVaultId.value, albumId: props.album?.id, options: {
    favouritesOnly: values.favouritesOnly,
    groupByAlbum: values.groupByAlbum,
    groupByGuest: values.groupByGuest,
  } }, {
    onSuccess: () => emit("zipRequested"),
    onError: () => toast.error(),
  })
})
</script>

<template>
  <Drawer :name="drawerKey" :title="t('zip_export_drawer.title')">
    <div v-if="isLoading" class="flex flex-col gap-5">
      <div class="gpx-skeleton w-full h-24" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-1/3 h-12" />
    </div>
    <div v-else-if="album || eventVault">
      <DrawerContextCard :event-vault="eventVault" :album="album" />
      <p class="mb-8">
        {{ t("zip_export_drawer.description", { type: album?.id ? t("album") : t("event") }) }}
      </p>
      <ZipStateSection
        :zip-summary="zipSummary"
        :is-generating="isGeneratingZip"
        mode="export"
        show-metadata
      >
        <template #default="{ zipState }">
          <form @submit.prevent="onGenerateZip">
            <div class="flex flex-col gap-4">
              <div class="flex flex-col gap-4" :class="{ 'opacity-60 pointer-events-none': isFavouritesOnly }">
                <div>
                  <Switch
                    name="groupByGuest"
                    :label="t('zip_export_drawer.guest_folders')"
                    :disabled="isZipPending || isFavouritesOnly"
                  />
                </div>
                <div v-if="!album?.id">
                  <Switch
                    name="groupByAlbum"
                    :label="t('zip_export_drawer.album_folders')"
                    :disabled="isZipPending || isFavouritesOnly"
                  />
                </div>
              </div>

              <div v-if="!hideFavourites">
                <Switch
                  name="favouritesOnly"
                  :label="t('zip_export_drawer.favourites_only')"
                  :disabled="isZipPending"
                />
                <p v-if="isFavouritesOnly" class="text-sm flex items-start gap-2 text-gray-500 mt-2">
                  <Icon icon="CircleInfo" size="sm" /> {{ t("zip_export_drawer.favourites_only_hint") }}
                </p>
              </div>
              <Alert v-if="zipState === 'ready'" :closable="false">
                {{ t("zip_export_drawer.export_alert") }}
              </Alert>
              <div class="flex gap-4">
                <Button
                  :disabled="isZipPending"
                  class="w-1/2" data-test="generate_button"
                >
                  {{ zipState === 'ready' ? t("zip_export_drawer.regenerate") : t("zip_export_drawer.generate") }}
                </Button>
                <Button
                  v-if="zipState === 'ready' && hasDownloadUrl && zipSummary?.downloadUrl"
                  :href="zipSummary.downloadUrl"
                  class="w-1/2"
                  variant="secondary"
                  @click.stop
                >
                  {{ t("download") }}
                </Button>
              </div>
            </div>
          </form>
        </template>
      </ZipStateSection>
    </div>
    <NoData v-else :button-label="t('close')" @click="toggle" />
  </Drawer>
</template>
